import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { InternalAuditResultByIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetInternalAuditResultByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC internal audit data to match the GraphQL query structure
 */
const mapTrpcInternalAuditToGraphQL = (
  trpcData: InternalAuditResultByIdResponseRow[]
): GetInternalAuditResultByIdQuery => {
  return {
    internal_audit_result_parent: trpcData,
  };
};

export const useGetInternalAuditResultsById = (
  internalAuditResultId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.internalAuditResult.internalAuditResultById.queryOptions({
      internalAuditResultId,
    }),
    enabled: trpcEnabled && !skip,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData ? mapTrpcInternalAuditToGraphQL(trpcData) : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
